/**
 * "Från din övningstenta" (pedagogy spec §7.3.7, E7): the suggestions the student added to the day's
 * session from the result page of an exam. The server turns the saved causes into practice links
 * (`extras` of `GET /api/practice/today`, kept for a week), so this only draws them: a title, one
 * line and a "Starta" that follows the link. The "Vilken princip?" set and the timed pair start by
 * themselves from there; the set-up and accuracy rows open a short batch on their topics.
 *
 * `href` comes from the API and is followed only when it is a practice link.
 */
import { useId } from "react";
import { Link } from "react-router";
import { useT } from "@client/i18n";
import { PRIMARY_LINK } from "./linkClass";
import type { TodayPlanDto } from "./types";

/** The dictionary segment of each suggestion kind (`practice.today.extra.<segment>.title`). */
const KIND_KEY: Record<string, string> = {
  "which-principle": "whichPrinciple",
  "timed-pair": "timedPair",
  setup: "setup",
  accuracy: "accuracy",
};

/** A link the practice page reads; anything else from the API is shown without a "Starta". */
export function isPracticeHref(href: string): boolean {
  return /^\/practice(\?[^\s]*)?$/.test(href);
}

export interface TodayExtrasProps {
  extras: TodayPlanDto["extras"];
  /** The heading level under the page's own: 3 inside Home's card (it has a title), 2 in the runner. */
  headingLevel?: 2 | 3;
}

export function TodayExtras({ extras, headingLevel = 3 }: TodayExtrasProps) {
  const t = useT();
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const headingId = useId();
  const baseId = useId();
  if (extras.length === 0) return null;
  return (
    <section
      aria-labelledby={headingId}
      className="mt-4 border-t border-ink-200 pt-4 dark:border-ink-800"
    >
      <Heading id={headingId} className="text-sm font-semibold text-ink-900 dark:text-ink-50">
        {t("practice.today.extras.title")}
      </Heading>
      <p className="mt-1 text-xs text-ink-600 dark:text-ink-400">
        {t("practice.today.extras.description")}
      </p>
      <ul aria-label={t("practice.today.extras.label")} className="mt-3 space-y-3">
        {extras.map((extra, index) => {
          const segment = KIND_KEY[extra.kind];
          const titleId = `${baseId}-${index}`;
          return (
            <li key={extra.id} className="rounded-lg border border-ink-200 p-3 dark:border-ink-800">
              <p id={titleId} className="font-medium text-ink-900 dark:text-ink-50">
                {t(
                  segment
                    ? `practice.today.extra.${segment}.title`
                    : "practice.today.extra.generic",
                )}
              </p>
              {segment && (
                <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">
                  {t(`practice.today.extra.${segment}.body`)}
                </p>
              )}
              {isPracticeHref(extra.href) && (
                <Link to={extra.href} aria-describedby={titleId} className={`${PRIMARY_LINK} mt-3`}>
                  {t("practice.setup.start")}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
